import { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';
import { debounce } from '../utils/formatters';

/**
 * Custom hook for managing note functionality
 * @param {string} locataireId - The ID of the tenant
 * @param {object} initialNotes - The initial notes data
 * @returns {object} - Contains states and functions for managing notes
 */
const useNotes = (locataireId, initialNotes = {}) => {
  // Notes state management
  const [notes, setNotes] = useState({
    gestionPaiement: initialNotes?.gestionPaiement || '',
    compta2023: initialNotes?.compta2023 || '',
    privee: initialNotes?.privee || ''
  });
  
  // Status tracking for each note
  const [saveStatus, setSaveStatus] = useState({
    gestionPaiement: null, // null, 'saving', 'saved', 'error'
    compta2023: null,
    privee: null
  });
  
  // Refs for debounced functions
  const debouncedUpdateNotes = useRef({});
  
  // Field name mapping
  const fieldNameMap = {
    gestionPaiement: 'note gestion paiement',
    compta2023: 'note compta 2023',
    privee: 'note privé'
  };
  
  // Initialize debounced functions
  useEffect(() => {
    // Create a debounced function for each note type
    Object.keys(notes).forEach(noteType => {
      debouncedUpdateNotes.current[noteType] = debounce((value) => {
        updateNote(noteType, value);
      }, 800);
    });
  }, [locataireId]);
  
  // Function to update a specific note
  const updateNote = async (noteType, value) => {
    if (!locataireId) {
      console.error("ID du locataire non trouvé");
      return;
    }
    
    // Get the field name for the API
    const fieldName = fieldNameMap[noteType];
    if (!fieldName) {
      console.error(`Type de note inconnu: ${noteType}`);
      return;
    }
    
    // Show saving indicator
    setSaveStatus(prev => ({ ...prev, [noteType]: 'saving' }));
    
    try {
      await apiService.updateNote(locataireId, fieldName, value, 'locataires');
      
      // Update save status to 'saved'
      setSaveStatus(prev => ({ ...prev, [noteType]: 'saved' }));
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [noteType]: null }));
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la note:', error);
      
      // Update save status to 'error'
      setSaveStatus(prev => ({ ...prev, [noteType]: 'error' }));
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [noteType]: null }));
      }, 3000);
    }
  };
  
  // Function to handle note changes
  const handleNoteChange = (noteType, value) => {
    // Update local state
    setNotes(prev => ({
      ...prev,
      [noteType]: value
    }));
    
    // Trigger debounced update
    if (debouncedUpdateNotes.current[noteType]) {
      debouncedUpdateNotes.current[noteType](value);
    }
  };
  
  return {
    notes,
    saveStatus,
    handleNoteChange
  };
};

export default useNotes;
